import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, MapPinIcon, ClockIcon, CurrencyDollarIcon, HashtagIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext.js';

const ParkingDetailsModal = ({ zone, isOpen, onClose }) => {
  const { t } = useLanguage();

  if (!zone) return null;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">{t('close')}</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10">
                    <MapPinIcon className="h-6 w-6 text-purple-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      {t('parkingZoneDetails')}
                    </Dialog.Title>
                    
                    <div className="mt-6 space-y-6">
                      {/* Zone Information */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">{t('basicInformation')}</h4>
                        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">{t('zoneName')}</dt>
                            <dd className="mt-1 text-sm text-gray-900">{zone.zoneName}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">{t('city')}</dt>
                            <dd className="mt-1 text-sm text-gray-900">{zone.city}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">{t('smsCode')}</dt>
                            <dd className="mt-1">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {zone.smsCode}
                              </span>
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">{t('smsPhone')}</dt>
                            <dd className="mt-1 text-sm text-gray-900">{zone.smsPhone}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">{t('zoneId')}</dt>
                            <dd className="mt-1 text-sm text-gray-900 font-mono">{zone.zoneId}</dd>
                          </div>
                        </dl>
                      </div>

                      {/* Pricing Information */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <CurrencyDollarIcon className="h-5 w-5 mr-2 text-yellow-600" />
                          {t('pricingInformation')}
                        </h4>
                        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">{t('ourPrice')}</dt>
                            <dd className="mt-1 text-lg font-semibold text-gray-900">
                              {zone.ourPrice} {zone.ourCurrency || 'EUR'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500 flex items-center">
                              <ClockIcon className="h-4 w-4 mr-1" />
                              {t('duration')}
                            </dt>
                            <dd className="mt-1 text-lg font-semibold text-gray-900">
                              {zone.timeInMinutes} {t('minutes')}
                            </dd>
                          </div>
                        </dl>
                      </div>

                      {/* Streets Information */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <HashtagIcon className="h-5 w-5 mr-2 text-green-600" />
                          {t('streets')} ({zone.streets?.length || 0})
                        </h4>
                        {zone.streets && zone.streets.length > 0 ? (
                          <div className="max-h-48 overflow-y-auto">
                            <ul className="space-y-2">
                              {zone.streets.map((street, index) => (
                                <li key={index} className="flex items-center text-sm text-gray-700">
                                  <span className="inline-block w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                                  {street}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">{t('noStreets')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={onClose}
                  >
                    {t('close')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ParkingDetailsModal;
